import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { FREE_MODE } from '../config/appConfig';
import ContactForm from '../components/ContactForm';
import SEO, { getOrganizationSchema, getWebsiteSchema } from '../components/SEO';
import { Building2, Search, FileText, ArrowRight, CheckCircle, Zap, Droplet, PaintBucket, Grid3x3, DoorOpen, Home, TreePine, Wind, Wrench, Hammer, Users, Newspaper, Star, Award, MapPin, Phone, ExternalLink, Flame, Layers, Mail, MessageSquare, ChevronDown, ChevronUp, Armchair, XCircle, UserPlus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Artykuł o remoncie mieszkania z rozwijalnym tekstem
const ArticleRemontMieszkania = () => {
  const [expanded, setExpanded] = useState(false);
  
  const shortText = `Remont mieszkania to proces wymagający precyzyjnego planowania, znajomości przepisów oraz realistycznego budżetu. W 2025/2026 roku średni koszt generalnego remontu 50-metrowego lokalu waha się od 80 000 zł do nawet 150 000 zł...`;
  
  const fullText = `Remont mieszkania to proces wymagający precyzyjnego planowania, znajomości przepisów oraz realistycznego budżetu. W 2025/2026 roku średni koszt generalnego remontu 50-metrowego lokalu waha się od 80 000 zł do nawet 150 000 zł.

1. Formalności i przepisy
Przed rozpoczęciem prac należy sprawdzić, czy planowane działania wymagają zgłoszenia:
• Zgłoszenie do spółdzielni/wspólnoty: W większości przypadków jest obowiązkowe, zwłaszcza gdy prace wpływają na części wspólne lub bezpieczeństwo budynku.
• Godziny prac: Hałaśliwe roboty (wiercenie, kucie) najlepiej prowadzić w dni robocze między 7:00 a 22:00.
• Informacja dla sąsiadów: Dobrą praktyką jest wywieszenie kartki z terminem zakończenia prac i numerem telefonu.

2. Kolejność prac (harmonogram)
Prawidłowa kolejność pozwala uniknąć poprawek i dodatkowych kosztów:
• Prace konstrukcyjne: Wyburzanie ścian, stawianie nowych ścianek działowych i montaż sufitów podwieszanych.
• Instalacje: Wymiana rur wodno-kanalizacyjnych oraz przewodów elektrycznych.
• Wylewki i tynki: Prace „mokre", które wymagają czasu na wyschnięcie.
• Wykończenie łazienki i kuchni: Montaż armatury, glazury oraz stałej zabudowy.
• Malowanie i podłogi: Zaleca się malowanie ścian przed montażem drzwi wewnętrznych, aby uniknąć zabrudzeń stolarki.

3. Kosztorysowanie
Przygotowując budżet, należy uwzględnić:
• Materiały: Wykończeniowe (farby, płytki) oraz pomocnicze (kleje, grunty).
• Robocizna: Może stanowić nawet 35–50% całkowitych kosztów remontu.
• Usługi dodatkowe: Wywóz gruzu, transport mebli czy wynajem kontenera.

Szacunkowe koszty remontu (2025/26):
• Generalny remont (całość): 1 500 – 5 000 zł / m²
• Remont łazienki (średni standard): 4 000 – 5 000 zł / m²
• Robocizna (przy 50 m²): ok. 50 000 zł`;

  return (
    <div>
      <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
        {expanded ? fullText : shortText}
      </p>
      <button
        onClick={() => setExpanded(!expanded)}
        className="mt-4 inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium transition-colors"
      >
        {expanded ? (
          <>
            Zwiń <ChevronUp className="w-4 h-4" />
          </>
        ) : (
          <>
            Czytaj dalej <ChevronDown className="w-4 h-4" />
          </>
        )}
      </button>
    </div>
  );
};

// Company logo component with fallback on error
const CompanyLogo = ({ logoUrl, name, size = 'md' }) => {
  const [hasError, setHasError] = useState(false);
  
  const sizeClasses = {
    sm: 'w-10 h-10',
    md: 'w-16 h-16',
    lg: 'w-24 h-24',
  };
  
  const iconSizes = {
    sm: 'w-5 h-5',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  };
  
  if (!logoUrl || hasError) {
    return (
      <div className={`${sizeClasses[size]} bg-orange-100 rounded-full flex items-center justify-center`}>
        <Building2 className={`${iconSizes[size]} text-orange-500`} />
      </div>
    );
  }
  
  return (
    <img 
      src={logoUrl} 
      alt={name} 
      className={`${sizeClasses[size]} rounded-full object-cover`}
      onError={() => setHasError(true)}
    />
  );
};

// Map category IDs to icons - każda kategoria ma unikalną ikonę
const categoryIcons = {
  remont: Hammer,           // Remonty ogólne
  elektryka: Zap,           // Elektryka
  hydraulika: Droplet,      // Hydraulika
  malowanie: PaintBucket,   // Malowanie i tapetowanie
  podlogi: Layers,          // Podłogi i posadzki
  dachy: Home,              // Dachy i pokrycia
  okna: DoorOpen,           // Okna i drzwi
  ogrzewanie: Flame,        // Ogrzewanie i CO
  wentylacja: Wind,         // Wentylacja i klimatyzacja
  meble: Armchair,          // Meble
  inne: Wrench,             // Inne prace
  // Aliases
  plytki: Grid3x3,
  stolarka: DoorOpen,
  dach: Home,
  ogrod: TreePine,
  klimatyzacja: Wind,
};

const newsCategoryColors = {
  news: 'bg-blue-100 text-blue-700',
  law: 'bg-purple-100 text-purple-700',
  tips: 'bg-green-100 text-green-700',
};

const newsCategoryNames = {
  news: 'Nowości',
  law: 'Prawo',
  tips: 'Porady',
};

const Landing = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ active_offers: 0, companies: 0, users: 0 });
  const [categories, setCategories] = useState([]);
  const [recentOffers, setRecentOffers] = useState([]);
  const [news, setNews] = useState([]);
  const [promotedCompanies, setPromotedCompanies] = useState([]);
  const [randomCompanies, setRandomCompanies] = useState([]);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showFooterContactForm, setShowFooterContactForm] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, categoriesRes, offersRes, newsRes, promotedRes, randomRes] = await Promise.all([
          fetch(`${API}/stats`),
          fetch(`${API}/categories`),
          fetch(`${API}/offers?limit=10`),
          fetch(`${API}/news?limit=6`),
          fetch(`${API}/promoted-companies?limit=5`),
          fetch(`${API}/companies?limit=20`)  // Get more companies for random rotation
        ]);
        if (statsRes.ok) setStats(await statsRes.json());
        if (categoriesRes.ok) setCategories(await categoriesRes.json());
        if (offersRes.ok) {
          const data = await offersRes.json();
          setRecentOffers(data.offers || []);
        }
        if (newsRes.ok) setNews(await newsRes.json());
        if (promotedRes.ok) setPromotedCompanies(await promotedRes.json());
        if (randomRes.ok) {
          const data = await randomRes.json();
          // Shuffle and pick companies for carousel
          const shuffled = (data.companies || []).sort(() => Math.random() - 0.5);
          setRandomCompanies(shuffled);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  // Carousel auto-rotation every 5 seconds
  useEffect(() => {
    if (randomCompanies.length <= 3) return;
    
    const interval = setInterval(() => {
      setCurrentCarouselIndex(prev => 
        (prev + 1) % Math.max(1, randomCompanies.length - 2)
      );
    }, 5000);
    
    return () => clearInterval(interval);
  }, [randomCompanies.length]);

  // Combined JSON-LD schemas for homepage
  const homepageSchema = [
    getOrganizationSchema(),
    getWebsiteSchema()
  ];

  return (
    <>
      {/* Pływający przycisk rejestracji - tylko dla niezalogowanych */}
      {!isAuthenticated && (
        <div 
          className="fixed bottom-6 right-6 z-[9999]"
          style={{ 
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 9999,
          }}
        >
          <Button 
            onClick={() => navigate('/register')} 
            className="bg-orange-500 hover:bg-orange-600 text-white shadow-xl hover:shadow-2xl transition-all rounded-full px-5 py-3 flex items-center gap-2 border-2 border-orange-400"
            data-testid="floating-register-btn"
            style={{ 
              WebkitTransform: 'translateZ(0)',
              transform: 'translateZ(0)',
            }}
          >
            <UserPlus className="w-4 h-4" />
            <span className="font-medium">Darmowa rejestracja</span>
          </Button>
        </div>
      )}
      
      <div className="min-h-screen bg-slate-50" data-testid="landing-page">
        <SEO 
          title="Ogłoszenia budowlane i remontowe"
          description="Znajdź sprawdzonych wykonawców budowlanych w całej Polsce. Publikuj zlecenia remontowe za darmo. Porównaj oferty firm: elektryka, hydraulika, remonty, malowanie i więcej."
          canonical="/"
          structuredData={homepageSchema}
        />
      
      {/* Hero Section */}
      <section className="relative bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src="https://static.prod-images.emergentagent.com/jobs/bf3da2f6-f024-408b-a4d6-fd5289f7b2dc/images/98b512fff6282385d8cc7bc096ed3ab536aeab15a642ae6985835c7da7e0ea3e.png" alt="Budowa" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="max-w-3xl">
              <h1 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl text-white tracking-tight leading-tight mb-6">
                Znajdź zlecenia i klientów budowlanych
                <span className="text-orange-500"> – za darmo</span>
              </h1>
              <p className="font-body text-lg md:text-xl text-slate-300 mb-8 max-w-2xl">
                Przeglądaj oferty w swoim mieście lub dodaj ogłoszenie w 2 minuty. Bez prowizji i ukrytych opłat.
              </p>
              
              {/* 3 główne ścieżki */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Ścieżka 1: Dla firm */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/15 transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-white font-semibold">Dla firm</span>
                  </div>
                  <p className="text-slate-300 text-sm mb-4">Stwórz profil firmy i docieraj do klientów</p>
                  <Button 
                    onClick={() => navigate(isAuthenticated ? '/company/profile' : '/register?type=company')} 
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold"
                    data-testid="hero-promote-company-btn"
                  >
                    Promuj firmę
                  </Button>
                </div>

                {/* Ścieżka 2: Dodaj ogłoszenie */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/15 transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-white font-semibold">Szukasz fachowca?</span>
                  </div>
                  <p className="text-slate-300 text-sm mb-4">Dodaj zlecenie i otrzymaj oferty od firm</p>
                  <Button 
                    onClick={() => navigate(isAuthenticated ? '/new-offer' : '/register')} 
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold"
                    data-testid="hero-add-offer-btn"
                  >
                    Dodaj ogłoszenie
                  </Button>
                </div>

                {/* Ścieżka 3: Znajdź wykonawcę */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/15 transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                      <Search className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-white font-semibold">Przeglądaj firmy</span>
                  </div>
                  <p className="text-slate-300 text-sm mb-4">Znajdź sprawdzonego wykonawcę w Twojej okolicy</p>
                  <Button 
                    onClick={() => navigate('/companies')} 
                    className="w-full bg-green-500 hover:bg-green-600 text-white font-bold"
                    data-testid="hero-find-contractor-btn"
                  >
                    Znajdź wykonawcę
                  </Button>
                </div>
              </div>

              {/* Link do rejestracji dla niezalogowanych */}
              {!isAuthenticated && (
                <p className="text-slate-400 text-center mt-6">
                  Nie masz konta? <a href="/register" className="text-orange-400 hover:text-orange-300 font-semibold underline">Zarejestruj się za darmo</a>
                </p>
              )}
          </div>
        </div>
      </section>

      {/* Sekcja korzyści - 4 punkty */}
      <section className="bg-white py-12 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="flex flex-col items-center text-center p-4">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="font-heading font-bold text-slate-900 mb-1">Darmowa rejestracja</h3>
              <p className="text-sm text-slate-500">Bez opłat za konto</p>
            </div>
            <div className="flex flex-col items-center text-center p-4">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <Users className="w-7 h-7 text-orange-600" />
              </div>
              <h3 className="font-heading font-bold text-slate-900 mb-1">Bez pośredników</h3>
              <p className="text-sm text-slate-500">Kontakt bezpośredni</p>
            </div>
            <div className="flex flex-col items-center text-center p-4">
              <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <MapPin className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="font-heading font-bold text-slate-900 mb-1">Oferty z całej Polski</h3>
              <p className="text-sm text-slate-500">Wszystkie województwa</p>
            </div>
          </div>
        </div>
      </section>

      {/* Karuzela losowych firm - rotacja co 5 sekund */}
      {randomCompanies.length > 0 && (
        <section className="py-10 bg-gradient-to-r from-slate-50 via-white to-slate-50" data-testid="company-carousel">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-xl text-slate-900">Poznaj firmy budowlane</h2>
                  <p className="text-sm text-slate-500">Zarejestrowane w naszym serwisie</p>
                </div>
              </div>
              <Link to="/companies" className="text-sm text-orange-600 hover:text-orange-700 font-medium flex items-center gap-1">
                Zobacz wszystkie <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="relative overflow-hidden">
              <div 
                className="flex transition-transform duration-700 ease-in-out gap-4"
                style={{ 
                  transform: `translateX(-${currentCarouselIndex * (100 / 3)}%)`,
                }}
              >
                {randomCompanies.map((company, idx) => (
                  <Link 
                    key={company.company_id || idx} 
                    to={`/companies/${company.company_id}`}
                    className="flex-shrink-0 w-full sm:w-1/2 lg:w-1/3"
                  >
                    <Card className="border border-slate-200 hover:border-blue-400 hover:shadow-lg transition-all h-full bg-white">
                      <CardContent className="p-5">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {company.logo_url ? (
                              <img src={company.logo_url} alt={company.name} className="w-full h-full object-cover rounded-xl" />
                            ) : (
                              <Building2 className="w-8 h-8 text-slate-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-heading font-bold text-slate-900 truncate">{company.name}</h3>
                            {company.location && (
                              <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                                <MapPin className="w-3 h-3" />{company.location}
                              </p>
                            )}
                            {company.rating > 0 && (
                              <div className="flex items-center gap-1 mt-1">
                                <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                                <span className="text-xs font-medium text-slate-700">{company.rating.toFixed(1)}</span>
                              </div>
                            )}
                            {company.description && (
                              <p className="text-xs text-slate-500 mt-2 line-clamp-2">{company.description}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
              
              {/* Carousel indicators */}
              {randomCompanies.length > 3 && (
                <div className="flex justify-center gap-2 mt-4">
                  {Array.from({ length: Math.min(5, Math.ceil(randomCompanies.length / 3)) }).map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentCarouselIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        Math.floor(currentCarouselIndex / 1) === idx ? 'bg-blue-500' : 'bg-slate-300'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Sekcja "Dlaczego warto się zarejestrować?" */}
      {!isAuthenticated && (
        <section className="py-16 md:py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-slate-900 mb-3">
                Dlaczego warto się zarejestrować?
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Dołącz do tysięcy firm i klientów, którzy korzystają z naszego serwisu
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Phone className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-heading font-bold text-slate-900 mb-2">Pełne dane kontaktowe</h3>
                <p className="text-sm text-slate-600">Otrzymuj dostęp do numerów telefonów i adresów email zleceniodawców</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-heading font-bold text-slate-900 mb-2">Dodawaj ogłoszenia</h3>
                <p className="text-sm text-slate-600">Publikuj własne zlecenia i docieraj do wykonawców z całej Polski</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Search className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="font-heading font-bold text-slate-900 mb-2">Zarządzaj ofertami</h3>
                <p className="text-sm text-slate-600">Śledź swoje ogłoszenia, odpowiedzi i kontakty w jednym miejscu</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <Mail className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-heading font-bold text-slate-900 mb-2">Powiadomienia</h3>
                <p className="text-sm text-slate-600">Otrzymuj alerty o nowych zleceniach pasujących do Twojego profilu</p>
              </div>
            </div>
            
            <div className="text-center">
              <Button 
                onClick={() => navigate('/register')} 
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg px-10 py-6 shadow-lg"
                data-testid="why-register-cta"
              >
                Zarejestruj się za darmo <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <p className="text-sm text-slate-500 mt-4">Rejestracja zajmuje mniej niż minutę</p>
            </div>
          </div>
        </section>
      )}

      {/* News Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Newspaper className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-slate-900">Wiadomości ze świata budowy</h2>
              <p className="text-slate-600">Najnowsze nowinki, przepisy i ciekawostki</p>
            </div>
          </div>
          
          {news.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {news.map((article) => (
                <a
                  key={article.news_id}
                  href={article.source_url || '#'}
                  target={article.source_url ? '_blank' : '_self'}
                  rel="noopener noreferrer"
                  className="block group"
                  data-testid={`news-card-${article.news_id}`}
                >
                  <Card className="border border-slate-200 group-hover:border-orange-500/50 group-hover:shadow-md transition-all h-full flex flex-col cursor-pointer">
                    <CardContent className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge className={newsCategoryColors[article.category] || 'bg-slate-100 text-slate-700'}>
                          {newsCategoryNames[article.category] || article.category}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {new Date(article.created_at).toLocaleDateString('pl-PL')}
                        </span>
                      </div>
                      <h3 className="font-heading font-bold text-lg text-slate-900 mb-2 line-clamp-2 group-hover:text-orange-600 transition-colors">{article.title}</h3>
                      <p className="font-body text-sm text-slate-600 line-clamp-3 flex-1">{article.summary}</p>
                      
                      <div className="mt-4 flex items-center justify-between">
                        {article.source && (
                          <span className="text-xs text-slate-400">
                            {article.source}
                          </span>
                        )}
                        {article.source_url && (
                          <span className="inline-flex items-center gap-1 text-sm text-orange-600 group-hover:text-orange-700 font-medium transition-colors">
                            Czytaj więcej <ExternalLink className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </a>
              ))}
            </div>
          ) : (
            <Card className="border-dashed border-2 border-slate-200">
              <CardContent className="p-12 text-center">
                <Newspaper className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Wkrótce pojawią się tutaj najnowsze wiadomości ze świata budownictwa</p>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Remont mieszkania Section */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Home className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-slate-900">Remont mieszkania</h2>
              <p className="text-slate-600">Praktyczny poradnik planowania remontu</p>
            </div>
          </div>
          
          <Card className="border border-slate-200">
            <CardContent className="p-6 md:p-8">
              <ArticleRemontMieszkania />
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Coming Soon Section - Rozwój serwisu */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="coming-soon-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 mb-4">
              Rozwijamy się dla Ciebie
            </Badge>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Co planujemy w przyszłości?
            </h2>
            <p className="font-body text-lg text-slate-300 max-w-2xl mx-auto">
              Ciężko pracujemy nad nowymi funkcjami, które uczynią Giełdę Budowlaną 
              jeszcze lepszym miejscem dla branży budowlanej
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Materials - NOW AVAILABLE */}
            <Link to="/materials">
              <Card className="bg-white/5 backdrop-blur border-emerald-500/50 hover:bg-white/10 transition-all group cursor-pointer">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <Building2 className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-white mb-3">
                    Giełda Materiałów
                  </h3>
                  <p className="font-body text-slate-300 leading-relaxed">
                    Marketplace dla materiałów budowlanych. Sprzedawaj lub kupuj 
                    cegły, płytki, farby, izolacje i wiele innych. 
                    Oszczędzaj pieniądze - daj materiałom drugie życie!
                  </p>
                  <div className="mt-6 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                    <span className="text-sm text-emerald-400 font-semibold">Dostępne teraz!</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
            
            {/* Jobs - NOW AVAILABLE */}
            <Link to="/jobs">
              <Card className="bg-white/5 backdrop-blur border-violet-500/50 hover:bg-white/10 transition-all group cursor-pointer">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-violet-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <Users className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-white mb-3">
                    Giełda Pracy
                  </h3>
                  <p className="font-body text-slate-300 leading-relaxed">
                    Sekcja dla firm szukających pracowników. 
                    Publikuj ogłoszenia o pracę dla murarzy, elektryków, hydraulików 
                    i innych specjalistów. Znajdź zatrudnienie w swojej okolicy.
                  </p>
                  <div className="mt-6 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-violet-500"></div>
                    <span className="text-sm text-violet-400 font-semibold">Dostępne teraz!</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
            
            {/* Cost Calculator Feature - COMING SOON */}
            <Card className="bg-white/5 backdrop-blur border-white/10 hover:bg-white/10 transition-all group">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <FileText className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading font-bold text-xl text-white mb-3">
                  Kalkulator kosztów
                </h3>
                <p className="font-body text-slate-300 leading-relaxed">
                  Wbudowane narzędzie do szacowania kosztów prac budowlanych i remontowych. 
                  Sprawdź orientacyjne ceny usług w Twojej okolicy, porównaj stawki rynkowe 
                  i lepiej zaplanuj budżet swojego projektu przed rozpoczęciem prac.
                </p>
                <div className="mt-6 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></div>
                  <span className="text-sm text-orange-400">Wkrótce</span>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-slate-400 mb-6">
              Masz pomysł na nową funkcję? Chętnie wysłuchamy Twojej opinii!
            </p>
            {showContactForm ? (
              <div className="max-w-md mx-auto">
                <ContactForm onClose={() => setShowContactForm(false)} title="Podziel się opinią" />
              </div>
            ) : (
              <Button 
                onClick={() => setShowContactForm(true)} 
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="open-contact-form"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Napisz do nas
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Promoted Companies Section */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
              <Award className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-slate-900">Firmy godne polecenia</h2>
              <p className="text-slate-600">Sprawdzone firmy budowlane z naszego portalu</p>
            </div>
          </div>
          
          {promotedCompanies.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {promotedCompanies.map((company) => (
                <Link key={company.company_id} to={`/companies/${company.company_id}`}>
                  <Card className="border-2 border-orange-200 hover:border-orange-500 transition-colors h-full bg-white shadow-sm hover:shadow-md">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-center mb-4">
                        <CompanyLogo logoUrl={company.logo_url} name={company.name} />
                      </div>
                      <h3 className="font-heading font-bold text-center text-slate-900 mb-2 line-clamp-2">{company.name}</h3>
                      {company.location && (
                        <p className="text-xs text-slate-500 text-center flex items-center justify-center gap-1">
                          <MapPin className="w-3 h-3" />{company.location}
                        </p>
                      )}
                      {company.rating > 0 && (
                        <div className="flex items-center justify-center gap-1 mt-2">
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          <span className="text-sm font-medium">{company.rating.toFixed(1)}</span>
                        </div>
                      )}
                      <div className="mt-3 flex flex-wrap gap-1 justify-center">
                        {company.services?.slice(0, 2).map((service, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">{service}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <Card className="border-dashed border-2 border-orange-200 bg-orange-50/50">
              <CardContent className="p-12 text-center">
                <Award className="w-12 h-12 text-orange-300 mx-auto mb-4" />
                <p className="text-slate-600 font-medium">Tutaj pojawią się najlepiej oceniane firmy budowlane</p>
                <p className="text-sm text-slate-500 mt-2">Jesteś firmą? Zbieraj pozytywne opinie od klientów i pokaż się tutaj!</p>
                <p className="text-xs text-slate-400 mt-1">Wyświetlamy firmy z najwyższą oceną i minimum jedną opinią</p>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mb-4">Jak to działa?</h2>
            <p className="font-body text-slate-600 max-w-2xl mx-auto">W trzech prostych krokach znajdziesz idealnego wykonawcę</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="font-heading font-bold text-2xl text-orange-500">1</span>
              </div>
              <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">Opisz zlecenie</h3>
              <p className="font-body text-slate-600">Dodaj szczegóły projektu, lokalizację i termin realizacji - za darmo!</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="font-heading font-bold text-2xl text-orange-500">2</span>
              </div>
              <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">Otrzymaj oferty</h3>
              <p className="font-body text-slate-600">Sprawdzone firmy budowlane wyślą Ci swoje propozycje</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="font-heading font-bold text-2xl text-orange-500">3</span>
              </div>
              <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">Wybierz wykonawcę</h3>
              <p className="font-body text-slate-600">Porównaj oferty, sprawdź opinie i wybierz najlepszą firmę</p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mb-4">Kategorie usług</h2>
            <p className="font-body text-slate-600 max-w-2xl mx-auto">Znajdź specjalistów w każdej dziedzinie budownictwa</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {categories.map((category) => {
              const IconComponent = categoryIcons[category.id] || Wrench;
              return (
                <Link key={category.id} to={`/offers?category=${category.id}`} className="group" data-testid={`category-${category.id}`}>
                  <Card className="border border-slate-200 hover:border-orange-500/50 transition-colors card-hover">
                    <CardContent className="p-6 text-center">
                      <IconComponent className="w-8 h-8 text-slate-600 group-hover:text-orange-500 mx-auto mb-3 transition-colors" />
                      <p className="font-body font-medium text-sm text-slate-700">{category.name}</p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Recent Offers */}
      {recentOffers.length > 0 && (
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading font-bold text-3xl text-slate-900 mb-2">Najnowsze ogłoszenia</h2>
                <p className="font-body text-slate-600">Zobacz co szukają nasi użytkownicy</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {recentOffers.map((offer) => (
                <Link key={offer.offer_id} to={`/offers/${offer.offer_id}`}>
                  <Card className="border border-slate-200 hover:border-orange-500/50 transition-colors card-hover h-full">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-2">
                        <Badge variant="secondary" className="text-xs">{categories.find(c => c.id === offer.category)?.name || offer.category}</Badge>
                        <span className="text-xs text-slate-500">{new Date(offer.created_at).toLocaleDateString('pl-PL')}</span>
                      </div>
                      <h3 className="font-heading font-bold text-base text-slate-900 mb-2 line-clamp-2">{offer.title}</h3>
                      <p className="font-body text-sm text-slate-600 mb-3 line-clamp-2">{offer.description}</p>
                      <div className="flex items-center text-sm">
                        <MapPin className="w-3 h-3 mr-1 text-slate-400" />
                        <span className="text-slate-500 text-xs">{offer.location}</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
            {/* Link do wszystkich ogłoszeń */}
            <div className="mt-10 text-center">
              <Button onClick={() => navigate('/offers')} className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3" data-testid="see-all-offers-btn">
                <Search className="w-4 h-4 mr-2" />
                Zobacz wszystkie ogłoszenia
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* For Companies CTA - ukryte w trybie darmowym */}
      {!FREE_MODE && (
        <section className="py-16 md:py-24 bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 mb-6">Dla firm budowlanych</Badge>
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">Zdobywaj nowe zlecenia każdego dnia</h2>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="font-body text-slate-300">Dostęp do tysięcy ogłoszeń budowlanych</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="font-body text-slate-300">Prezentuj swoją firmę i portfolio zdjęć</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="font-body text-slate-300">Zbieraj opinie i buduj reputację</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="font-body text-slate-300">Stała subskrypcja tylko 15 zł netto/mies.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="font-body text-slate-300">Opcja promowania firmy - 25 zł netto/7 dni</span>
                  </li>
                </ul>
                <Button onClick={() => navigate('/register?type=company')} className="bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg px-8 py-6" data-testid="cta-company-register">
                  Zarejestruj firmę<ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
              <div className="hidden md:block">
                <img src="https://static.prod-images.emergentagent.com/jobs/bf3da2f6-f024-408b-a4d6-fd5289f7b2dc/images/80bfe30c1dba9a09feed0d80887cbbf2021e6c9180d2eac77d36688113291e5f.png" alt="Pracownik budowlany" className="rounded-lg shadow-2xl" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-orange-500 rounded flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <span className="font-heading font-bold text-white text-sm">Giełda Budowlana</span>
              </div>
              <p className="text-sm">Ogólnopolski zbiór ofert i firm budowlanych.</p>
            </div>
            <div>
              <h4 className="font-heading font-bold text-white mb-4">Dla klientów</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/offer-choice" className="hover:text-white transition-colors">Dodaj ogłoszenie</Link></li>
                <li><Link to="/companies" className="hover:text-white transition-colors">Znajdź firmę</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-bold text-white mb-4">Dla firm</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/register?type=company" className="hover:text-white transition-colors">Zarejestruj firmę</Link></li>
                <li><Link to="/offers" className="hover:text-white transition-colors">Znajdź zlecenia</Link></li>
                {!FREE_MODE && (
                  <li><Link to="/subscription" className="hover:text-white transition-colors">Subskrypcja</Link></li>
                )}
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-bold text-white mb-4">Regulaminy</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/regulamin" className="hover:text-white transition-colors">Regulamin serwisu</Link></li>
                {!FREE_MODE && (
                  <li><Link to="/regulamin-platnosci" className="hover:text-white transition-colors">Regulamin usług płatnych</Link></li>
                )}
                <li><Link to="/polityka-prywatnosci" className="hover:text-white transition-colors">Polityka prywatności</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-bold text-white mb-4">Kontakt</h4>
              <ul className="space-y-2 text-sm">
                <li className="font-medium text-white">Piotr Kowalczyk P&P Profinish</li>
                <li>kontakt@giełdabudowlana.pl</li>
                <li>+48 797-595-552</li>
                <li>ul. Banderii 4/276</li>
                <li>01-164 Warszawa</li>
              </ul>
              <Button 
                onClick={() => setShowFooterContactForm(true)} 
                variant="outline" 
                size="sm" 
                className="mt-4 border-slate-600 text-slate-300 hover:text-white hover:border-white"
                data-testid="footer-contact-btn"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Napisz do nas
              </Button>
            </div>
          </div>

          {/* Linkowanie wewnętrzne - lokalne strony SEO */}
          <div className="border-t border-slate-800 mt-8 pt-8">
            <h4 className="font-heading font-bold text-white mb-4 text-sm">Budownictwo w Twoim mieście</h4>
            <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
              {['warszawa', 'krakow', 'lodz', 'wroclaw', 'poznan', 'gdansk', 'szczecin', 'katowice'].map((slug) => (
                <Link key={slug} to={`/praca-budowlana/${slug}`} className="hover:text-white transition-colors capitalize">
                  Praca budowlana {slug.replace('-', ' ')}
                </Link>
              ))}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm mt-3">
              <Link to="/praca-budowlana" className="text-orange-400 hover:text-orange-300">Praca budowlana - wszystkie miasta</Link>
              <Link to="/remonty" className="text-orange-400 hover:text-orange-300">Remonty - wszystkie miasta</Link>
              <Link to="/materialy-budowlane" className="text-orange-400 hover:text-orange-300">Materiały budowlane - wszystkie miasta</Link>
            </div>
          </div>

          {/* Footer Contact Form Modal */}
          {showFooterContactForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="w-full max-w-md">
                <ContactForm onClose={() => setShowFooterContactForm(false)} title="Skontaktuj się z nami" />
              </div>
            </div>
          )}
          
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm">
            <p>&copy; 2025 Giełda Budowlana. Wszystkie prawa zastrzeżone.</p>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default Landing;
